import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useApiResource } from "../hooks/useApiResource";
import Layout from "../components/Layout";
import RecommendationCard from "../components/RecommendationCard";
import StarRating from "../components/StarRating";
import BestTripHero from "../components/BestTripHero";
import { useAuth } from "../context/AuthContext";
import { Sparkle } from "lucide-react";

const TABS = [
    { key: "this", label: "This Season", testid: "tab-this" },
    { key: "next", label: "Next Season", testid: "tab-next" },
    { key: "best6", label: "Best · 6 Months", testid: "tab-best-6" },
    { key: "best12", label: "Best · 12 Months", testid: "tab-best-12" },
];

export default function Dashboard() {
    const { user } = useAuth();
    const [tab, setTab] = useState("this");
    const isSeasonTab = tab === "this" || tab === "next";
    const season = tab === "next" ? "next" : "this";
    const { data, loading, error } = useApiResource(
        isSeasonTab ? `/recommendations?season=${season}` : null
    );
    const [star, setStar] = useState(null);

    useEffect(() => {
        api.get("/star-power").then((r) => setStar(r.data)).catch(() => {});
    }, []);

    const chart = user?.natal_chart;

    return (
        <Layout>
            <div className="sp-container pt-12 pb-8">
                <div className="flex items-start justify-between gap-8 flex-col lg:flex-row">
                    <div>
                        <div className="sp-overline" data-testid="dashboard-greeting">Hello{user?.name ? `, ${user.name.split(" ")[0]}` : ""}</div>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif tracking-tight mt-3 leading-[1.05]">
                            Where the sky is calling you.
                        </h1>
                        {chart && (
                            <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="dashboard-chart-pills">
                                <span className="sp-pill"><Sparkle strokeWidth={1.25} className="w-3 h-3 text-planet-sun" /> Sun in {chart.sun}</span>
                                <span className="sp-pill"><Sparkle strokeWidth={1.25} className="w-3 h-3 text-planet-moon" /> Moon in {chart.moon}</span>
                                <span className="sp-pill"><Sparkle strokeWidth={1.25} className="w-3 h-3 text-planet-venus" /> {chart.rising} Rising</span>
                            </div>
                        )}
                    </div>

                    {star && (
                        <div className="bg-surface border border-ink/10 rounded-2xl p-6 w-full lg:w-80" data-testid="dashboard-star-power">
                            <div className="sp-overline">Your Star Power</div>
                            <div className="mt-2"><StarRating score={star.score} stars={star.stars} size="md" testid="dashboard-star-power-rating" /></div>
                            <div className="mt-4 text-xs text-ink-muted">Last 30 days · {star.active_days} active day{star.active_days === 1 ? "" : "s"}</div>
                        </div>
                    )}
                </div>

                <div className="mt-12 flex items-center gap-6 sm:gap-8 border-b border-ink/10 overflow-x-auto">
                    {TABS.map((t) => (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            className={`relative pb-4 text-sm tracking-wide transition-colors whitespace-nowrap ${tab === t.key ? "text-ink" : "text-ink-muted hover:text-ink"}`}
                            data-testid={t.testid}
                        >
                            {t.label}
                            {tab === t.key && <span className="absolute left-0 right-0 -bottom-px h-px bg-ink" />}
                        </button>
                    ))}
                    {isSeasonTab && data && (
                        <span className="ml-auto text-xs sp-overline" data-testid="dashboard-season-label">
                            {tab === "this" ? data.this_season_label : data.next_season_label}
                        </span>
                    )}
                </div>
            </div>

            <div className="sp-container pb-24">
                {!isSeasonTab ? (
                    <BestTripHero
                        months={tab === "best6" ? 6 : 12}
                        title={tab === "best6" ? "Your Best Trip in the Next 6 Months" : "Your Best Trip in the Next 12 Months"}
                    />
                ) : (
                    <>
                        {error && <div className="text-sm text-red-700" data-testid="dashboard-error">{error}</div>}
                        {loading && (
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
                                {[0, 1, 2, 3, 4, 5].map((i) => (
                                    <div key={i} className="sp-card animate-pulse" data-testid={`dashboard-skeleton-${i}`}>
                                        <div className="aspect-[4/3] bg-sand-100" />
                                        <div className="p-6 space-y-3">
                                            <div className="h-4 bg-sand-100 rounded w-24" />
                                            <div className="h-6 bg-sand-100 rounded w-3/4" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        {!loading && data && (
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8" data-testid="dashboard-recommendations">
                                {data.recommendations.map((rec) => (
                                    <RecommendationCard key={rec.id} rec={rec} season={season} testidPrefix="dashboard-rec" />
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        </Layout>
    );
}
